import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'

import { useMediaQueryContext } from 'contexts/MediaQueryContext'
import { media } from 'styles/media'

import MetaTags from '../../../components/meta/MetaTags'
import ScrollTop from '../../../components/utils/ScrollTop'
import MapDetailedIcon from '../../../components/icons/MapDetailed'
import CameraProDetailed from '../../../components/icons/CameraProDetailed'
import Celebrate from '../../../components/icons/Celebrate'
import SimpleA from '../../../components/display/SimpleAnchor'
import ButtonRouterLink from '../../../components/display/ButtonRouterLink'
import {
  ResponsiveTwoColumnLayout,
  SpacedColumn,
} from '../../../components/landing/display'
import { FixedColumnsMasonry } from '../../../components/landing/MasonryImages'

import Values from '../../../components/branding/Values'
import { LargeVideo } from '../../../components/branding/VideoBanner'

import {
  Padded,
  SectionTitle,
  SectionSubtitle,
  SectionParagraph,
  SectionOuter,
} from '../section'

import Header from './Header'
import FulfillingMomentGrid from './FulfillingMomentsGrid'
import PressLogos from './PressLogos'
import {
  IllustratedParagraph,
  Wrapper as IllustratedParagraphsWrapper,
} from '../IllustratedParagraph'

const ourPeopleImages = [
  {
    medium:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_1_medium_2022-04-11.jpg',
    large:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_1_large_2022-04-11.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_2_medium_2022-04-11.jpg',
    large:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_2_large_2022-04-11.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_3_medium_2022-04-11.jpg',
    large:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_3_large_2022-04-11.jpg',
  },
  {
    medium:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_4_medium_2022-04-11.jpg',
    large:
      'https://assets.riparide.com/landing_pages/about_us/our_people/our_people_4_large_2022-04-11.jpg',
  },
]

const PressLogosWrapper = styled.div`
  margin-top: 48px;

  ${media.md`
    margin-top: 72px;
  `}
`

const VideoWrapper = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  margin-bottom: 30px;

  ${media.md`
    margin-bottom: 72px;
  `}
`

const MomentCount = styled.h5`
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-lg']?.['font-size']?.value};
  font-weight: 400;
  margin-top: 48px;
  margin-bottom: 16px;

  ${media.md`
    margin-left: auto;
    margin-right: auto;
    margin-top: 64px;
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xl']?.['font-size']?.value};
    max-width: 640px;
  `}
`

const About = () => {
  const theme = useTheme()
  const { md: largerThanMd } = useMediaQueryContext()

  const [t] = useTranslation('spelling')

  return (
    <>
      <ScrollTop />
      <MetaTags title="About Us" />

      <Header />

      <SectionOuter>
        <Padded>
          <SectionTitle>Our Purpose</SectionTitle>
          <SectionSubtitle>
            Inspiring you to find {largerThanMd && <br />} fulfilling moments in
            nature
          </SectionSubtitle>
          <SectionParagraph>
            Riparide exists to inspire you to unplug from modern-day trappings
            and find fulfillment in nature. Why? Because time in nature fills
            the soul.
          </SectionParagraph>

          <MomentCount>
            Over 200,000 fulfilling moments — and counting!
          </MomentCount>

          <SectionParagraph>
            Since 2018, we’ve been measuring {t('traveler')} fulfillment. When
            you return home, we ask you to share which moments of fulfillment
            you experienced during your escape.
          </SectionParagraph>
        </Padded>
        <FulfillingMomentGrid style={{ margin: '40px auto' }} />
      </SectionOuter>

      <SectionOuter>
        <Padded>
          <SectionTitle>What We Do Differently</SectionTitle>
          <SectionSubtitle>We tell Stories of adventure</SectionSubtitle>

          <IllustratedParagraphsWrapper>
            <IllustratedParagraph
              heading="Travel Stories"
              text={
                <>
                  Riparide offers a curated collection of travel{' '}
                  <SimpleA href="/stories-travellers">Stories</SimpleA>,
                  published by real travellers. Each Story comes packed with
                  adventure tips, social vibes and unique accommodation.
                </>
              }
              icon={<CameraProDetailed />}
            />
            <IllustratedParagraph
              heading="Short escapes"
              text="Riparide is all about bite-sized, nature escapes that trade maximum experience on minimum time. Taking regular short breaks helps us sustain our busy lifestyles and live a more fulfilled life."
              icon={<MapDetailedIcon />}
            />
            <IllustratedParagraph
              heading="Fulfilling moments"
              text="We’re on a mission to deliver fulfilling moments in nature and we’re measuring it. By the end of 2030, we aim to have delivered over 1 million moments of fulfillment."
              icon={<Celebrate />}
            />
          </IllustratedParagraphsWrapper>

          <ButtonRouterLink
            to="/how-it-works"
            uppercase={false}
            style={{ margin: '20px auto 0 auto' }}
          >
            How it works
          </ButtonRouterLink>
        </Padded>
      </SectionOuter>

      <SectionOuter>
        <Padded>
          <VideoWrapper>
            <LargeVideo />
          </VideoWrapper>

          <SectionTitle>Our Story</SectionTitle>
          <SectionSubtitle>It started with a big city battle</SectionSubtitle>
          <SectionParagraph>
            Co-founder Marlon Law grew up in the mountains and by the ocean, but
            he lost that connection with nature when he moved to the city.
            <br />
            <br />
            He would escape to his beach property at every opportunity,{' '}
            {t('realizing')} that this coastal getaway was the perfect antidote
            to his urban lifestyle. But he wanted to share its magic with others
            too.
            <br />
            <br />
            Marlon set up a glamping tent and in doing so, Riparide got its
            first Story, documented by a local photographer. Marlon saw
            Storytelling as a creative way to bring to life the experience the
            tent offered, and Riparide was born.
          </SectionParagraph>
        </Padded>
      </SectionOuter>

      <SectionOuter>
        <Padded>
          <SectionTitle>Our Values</SectionTitle>
          <SectionSubtitle>These are our guiding principles</SectionSubtitle>
        </Padded>
        <Values />
      </SectionOuter>

      <div
        style={{
          backgroundColor: theme?.['-ds-neutral-200']?.value,
          padding: '1px', // hack to make section margin fit inside div bounds
        }}
      >
        <SectionOuter>
          <Padded>
            <SectionTitle>In The Press</SectionTitle>
            <SectionSubtitle>Spotlight on Riparide</SectionSubtitle>
          </Padded>
          <PressLogosWrapper>
            <PressLogos />
          </PressLogosWrapper>
        </SectionOuter>
      </div>

      <SectionOuter>
        <ResponsiveTwoColumnLayout
          style={{ maxWidth: '1300px', margin: '0 auto' }}
        >
          <SpacedColumn style={{ justifyContent: 'center' }}>
            <Padded style={{ textAlign: 'left', maxWidth: '528px' }}>
              <SectionTitle>Our People</SectionTitle>
              <SectionSubtitle>
                We’re a friendly and genuine bunch
              </SectionSubtitle>
              <SectionParagraph>
                The Riparide crew come from all over the world. We believe that
                time spent in nature can improve our well-being and help people
                lead more fulfilling lives. If you think you might be a good fit
                for our team, see our{' '}
                <SimpleA href="/jobs" target="_blank" rel="noopener noreferrer">
                  list of open positions.
                </SimpleA>
              </SectionParagraph>

              <ButtonRouterLink
                to="/team"
                uppercase={false}
                style={{ marginTop: '32px' }}
                ghost
              >
                Meet the team
              </ButtonRouterLink>
            </Padded>
          </SpacedColumn>

          <SpacedColumn>
            <Padded style={{ width: '100%' }}>
              <FixedColumnsMasonry
                columnsCount={2}
                gutter={largerThanMd ? 32 : 16}
                images={ourPeopleImages}
                imagesPerColumn={{
                  1: 2,
                  2: 2,
                  3: 2,
                }}
              />
            </Padded>
          </SpacedColumn>
        </ResponsiveTwoColumnLayout>
      </SectionOuter>
    </>
  )
}

export default About
